import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen.ts';

// Self-hosted Warm Studio typography. Cyrillic subset is needed for
// customer names and invoice notes in Russian; latin covers UI strings.
// Italic Lora is the editorial accent used in display titles.
import '@fontsource/manrope/cyrillic-400.css';
import '@fontsource/manrope/cyrillic-500.css';
import '@fontsource/manrope/cyrillic-600.css';
import '@fontsource/manrope/cyrillic-700.css';
import '@fontsource/manrope/latin-400.css';
import '@fontsource/manrope/latin-500.css';
import '@fontsource/manrope/latin-600.css';
import '@fontsource/manrope/latin-700.css';
import '@fontsource/lora/cyrillic-400.css';
import '@fontsource/lora/cyrillic-500.css';
import '@fontsource/lora/cyrillic-600.css';
import '@fontsource/lora/cyrillic-400-italic.css';
import '@fontsource/lora/latin-400.css';
import '@fontsource/lora/latin-500.css';
import '@fontsource/lora/latin-600.css';
import '@fontsource/lora/latin-400-italic.css';

import './index.css';

// One QueryClient per browser tab. Defaults are tuned for the auth-aware shell:
// auth state is checked once per tab/load and explicit invalidations (logout,
// callback) drive refetches — see src/client/lib/auth.ts for the rationale.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: Infinity,
    },
  },
});

const router = createRouter({
  routeTree,
  // Phase 1 routes have no per-route loader context yet; declared as `{}`
  // so we can extend without breaking changes.
  context: {},
  defaultPreload: 'intent',
});

// Register the concrete router instance for type-safe Link/useNavigate.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root not found');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
